// =============================================================================
// NewsDetailPage — rota /news/:id (substitui o modal antigo).
// Layout: header simples (logo + voltar) + hero da imagem + corpo HTML.
// Pode ser aberta direto pelo launcher (URL fixa por noticia).
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, Loader2, Newspaper } from 'lucide-react';
import { getNewsById, type NewsItem } from '@/shared/services/eventsService';
import { Brand } from '@/shared/components/liberty';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getNewsById(id)
      .then((r) => { if (!cancelled) setItem(r); })
      .catch((e: any) => {
        if (cancelled) return;
        const msg = e?.Message || e?.message || 'Notícia não encontrada.';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const fmtDate = useMemo(
    () => (d?: string | null) => (d ? new Date(d).toLocaleDateString('pt-BR') : ''),
    [],
  );

  return (
    <div className="lb-news min-h-screen flex flex-col bg-[#05080b] text-white">
      {/* === HEADER === */}
      <header className="border-b border-white/10 bg-black/55 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/news')}
            className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-[#00e1d7] transition-colors cursor-pointer"
          >
            <ChevronLeft size={16} /> Voltar às notícias
          </button>
          <Brand />
        </div>
      </header>

      {/* === MAIN === */}
      <main className="flex-1 mx-auto max-w-4xl w-full px-4 py-10">
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={28} className="animate-spin text-[#00e1d7]" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-10 text-center">
            <Newspaper size={36} className="mx-auto text-[#00e1d7]/40 mb-4" />
            <h1 className="text-2xl font-extrabold mb-2">{error}</h1>
            <p className="text-sm text-white/55 mb-6">
              Talvez ela tenha sido removida ou o link esteja incorreto.
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 rounded-xl bg-[#00e1d7] px-5 py-3 text-sm font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00e1d7]/90"
            >
              Ver todas as notícias
            </Link>
          </div>
        )}

        {!loading && !error && item && (
          <article className="rounded-2xl border border-white/10 bg-black/45 backdrop-blur-md overflow-hidden">
            {item.image && (
              <div className="relative h-72 md:h-96 w-full overflow-hidden">
                <img src={newsImg(item.image, 1280)} onError={newsImgFallback(item.image)} alt={item.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05080b] via-transparent to-transparent" />
              </div>
            )}

            <div className="p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-[#00e1d7]/15 text-[#00e1d7] border border-[#00e1d7]/30">
                  Notícia
                </span>
                <span className="text-[11px] text-white/40 font-medium tracking-wider">
                  {fmtDate(item.createdDate)}
                </span>
              </div>

              <h1
                className="lb-news-title"
              >
                {item.title}
              </h1>

              {item.subTitle && (
                <p className="mt-3 text-lg md:text-xl font-semibold text-[#00e1d7]/85">
                  {item.subTitle}
                </p>
              )}

              <div
                className="news-html mt-8 text-base text-white/75 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.html || '' }}
              />
            </div>
          </article>
        )}
      </main>

      {/* === FOOTER === */}
      <footer className="border-t border-white/8 bg-black/40 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-4 py-5 text-center">
          <p className="text-xs text-white/35">
            &copy; {new Date().getFullYear()} Liberty Games · Cabal EP39
          </p>
        </div>
      </footer>

      {/* Estiliza um pouco o HTML vindo do banco (titulos, listas, imagens) */}
      <style>{`
        .news-html h1, .news-html h2, .news-html h3 {
          color: #fff; font-weight: 800; margin-top: 1.5em; margin-bottom: 0.5em;
        }
        .news-html h2 { font-size: 1.5rem; }
        .news-html h3 { font-size: 1.25rem; }
        .news-html p { margin-bottom: 1em; }
        .news-html a { color: #8fb9de; text-decoration: underline; }
        .news-html ul, .news-html ol { margin: 1em 0 1em 1.5em; }
        .news-html li { margin-bottom: 0.4em; }
        .news-html img { max-width: 100%; border-radius: 8px; margin: 1em 0; }
        .news-html strong, .news-html b { color: #fff; }
        .news-html hr { margin: 2em 0; border-color: rgba(255,255,255,0.08); }
      `}</style>
    </div>
  );
}
